import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { useNavigate, useRouteLoaderData } from "@remix-run/react";
import Fuse from "fuse.js";
import { FileText, Search } from "lucide-react";

import { CATEGORY_MAP } from "~/components/help-center/categories";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { cn } from "~/lib/utils";

import type { HelpArticleNavItem } from "~/routes/help";

// AIDEV-NOTE: Quick-switcher search modal (Algolia-style) for help center articles.
// Uses Fuse.js for client-side fuzzy search over title, summary, and plainText.
// Split into Provider (state) + Dialog (modal) + Trigger (button) so the trigger
// can be placed in both the sidebar and the index page.

const SNIPPET_RADIUS = 80;

interface SnippetParts {
    before: string;
    match: string;
    after: string;
}

function extractSnippet(text: string, query: string): SnippetParts | null {
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const idx = lowerText.indexOf(lowerQuery);
    if (idx === -1) return null;

    const start = Math.max(0, idx - SNIPPET_RADIUS);
    const end = Math.min(text.length, idx + query.length + SNIPPET_RADIUS);

    const before = (start > 0 ? "\u2026" : "") + text.slice(start, idx);
    const match = text.slice(idx, idx + query.length);
    const after =
        text.slice(idx + query.length, end) +
        (end < text.length ? "\u2026" : "");

    return { before, match, after };
}

interface SearchResultItem {
    item: HelpArticleNavItem;
    snippet: SnippetParts | null;
}

// --- Context ---

interface HelpSearchContextValue {
    open: () => void;
}

const HelpSearchContext = createContext<HelpSearchContextValue | null>(null);

function useHelpSearch() {
    const ctx = useContext(HelpSearchContext);
    if (!ctx)
        throw new Error("useHelpSearch must be used within HelpSearchProvider");
    return ctx;
}

// --- Provider + Dialog (render once in the help layout) ---

export function HelpSearchProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const helpData = useRouteLoaderData("routes/help") as
        | { articles: HelpArticleNavItem[] }
        | undefined;
    const articles = helpData?.articles ?? [];

    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const fuse = useMemo(() => {
        try {
            return new Fuse(articles, {
                keys: [
                    { name: "title", weight: 3 },
                    { name: "summary", weight: 2 },
                    { name: "plainText", weight: 1 },
                ],
                threshold: 0.4,
                minMatchCharLength: 2,
                includeMatches: true,
            });
        } catch {
            return null;
        }
    }, [articles]);

    const results: SearchResultItem[] = useMemo(() => {
        if (!query.trim()) return [];
        if (!fuse) {
            const q = query.toLowerCase();
            return articles
                .filter(
                    (a) =>
                        a.title.toLowerCase().includes(q) ||
                        a.summary.toLowerCase().includes(q) ||
                        a.plainText.toLowerCase().includes(q),
                )
                .slice(0, 8)
                .map((a) => ({
                    item: a,
                    snippet: extractSnippet(a.plainText, query),
                }));
        }

        return fuse.search(query, { limit: 8 }).map((result) => {
            const bodyMatch = result.matches?.find(
                (m) => m.key === "plainText",
            );
            let snippet: SnippetParts | null = null;
            if (bodyMatch && result.item.plainText) {
                snippet = extractSnippet(result.item.plainText, query);
            }
            return { item: result.item, snippet };
        });
    }, [query, fuse, articles]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [results]);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => inputRef.current?.focus(), 50);
            return () => clearTimeout(timer);
        }
        setQuery("");
        setSelectedIndex(0);
    }, [isOpen]);

    useEffect(() => {
        if (!listRef.current) return;
        const items = listRef.current.querySelectorAll("[data-search-item]");
        items[selectedIndex]?.scrollIntoView({ block: "nearest" });
    }, [selectedIndex]);

    // Cmd+K / Ctrl+K global shortcut
    useEffect(() => {
        function handleGlobalKeyDown(e: KeyboardEvent) {
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                setIsOpen(true);
            }
        }
        document.addEventListener("keydown", handleGlobalKeyDown);
        return () =>
            document.removeEventListener("keydown", handleGlobalKeyDown);
    }, []);

    const navigateToResult = useCallback(
        (result: SearchResultItem) => {
            setIsOpen(false);
            navigate(`/help/${result.item.category}/${result.item.slug}`);
        },
        [navigate],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex((i) => (i < results.length - 1 ? i + 1 : 0));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex((i) => (i > 0 ? i - 1 : results.length - 1));
            } else if (e.key === "Enter" && results[selectedIndex]) {
                e.preventDefault();
                navigateToResult(results[selectedIndex]);
            }
        },
        [results, selectedIndex, navigateToResult],
    );

    const ctxValue = useMemo(() => ({ open: () => setIsOpen(true) }), []);

    return (
        <HelpSearchContext.Provider value={ctxValue}>
            {children}

            <Dialog
                open={isOpen}
                onOpenChange={setIsOpen}
                analyticsId="help-search"
            >
                <DialogContent
                    className="p-0 gap-0 max-w-xl top-[30%] translate-y-[-30%]"
                    showCloseButton={false}
                >
                    <DialogTitle className="sr-only">
                        Search help articles
                    </DialogTitle>

                    {/* Search input */}
                    <div className="flex items-center gap-3 border-b px-4 py-3">
                        <Search className="size-4 shrink-0 text-muted-foreground" />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search help articles..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        />
                    </div>

                    {/* Results list */}
                    {query.trim() && (
                        <div
                            ref={listRef}
                            className="max-h-[360px] overflow-y-auto"
                        >
                            {results.length > 0 ? (
                                <ul className="py-2">
                                    {results.map((result, i) => {
                                        const cat =
                                            CATEGORY_MAP[result.item.category];
                                        return (
                                            <li key={result.item.slug}>
                                                <button
                                                    type="button"
                                                    data-search-item
                                                    onClick={() =>
                                                        navigateToResult(result)
                                                    }
                                                    onMouseEnter={() =>
                                                        setSelectedIndex(i)
                                                    }
                                                    className={cn(
                                                        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors",
                                                        i === selectedIndex &&
                                                            "bg-accent",
                                                    )}
                                                >
                                                    <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-baseline gap-2">
                                                            <span className="text-sm font-medium truncate">
                                                                {
                                                                    result.item
                                                                        .title
                                                                }
                                                            </span>
                                                            {cat && (
                                                                <span className="shrink-0 text-[11px] text-muted-foreground">
                                                                    {cat.label}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {result.item
                                                            .summary && (
                                                            <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
                                                                {
                                                                    result.item
                                                                        .summary
                                                                }
                                                            </p>
                                                        )}
                                                        {result.snippet && (
                                                            <p className="mt-1 text-xs text-muted-foreground/70 line-clamp-2">
                                                                {
                                                                    result
                                                                        .snippet
                                                                        .before
                                                                }
                                                                <mark className="bg-yellow-200/80 text-foreground rounded-sm px-0.5">
                                                                    {
                                                                        result
                                                                            .snippet
                                                                            .match
                                                                    }
                                                                </mark>
                                                                {
                                                                    result
                                                                        .snippet
                                                                        .after
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                                    No articles found for &ldquo;{query}&rdquo;
                                </div>
                            )}
                        </div>
                    )}

                    {/* Empty state hint */}
                    {!query.trim() && (
                        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                            Start typing to search across all help articles
                        </div>
                    )}

                    {/* Footer with keyboard hints */}
                    <div className="flex items-center gap-4 border-t px-4 py-2 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border bg-muted px-1 py-0.5 font-mono">
                                &uarr;
                            </kbd>
                            <kbd className="rounded border bg-muted px-1 py-0.5 font-mono">
                                &darr;
                            </kbd>
                            navigate
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border bg-muted px-1 py-0.5 font-mono">
                                &crarr;
                            </kbd>
                            open
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border bg-muted px-1 py-0.5 font-mono">
                                esc
                            </kbd>
                            close
                        </span>
                    </div>
                </DialogContent>
            </Dialog>
        </HelpSearchContext.Provider>
    );
}

// --- Trigger button (place anywhere inside HelpSearchProvider) ---

interface HelpSearchTriggerProps {
    className?: string;
    compact?: boolean;
}

export function HelpSearchTrigger({
    className,
    compact,
}: HelpSearchTriggerProps) {
    const { open } = useHelpSearch();

    if (compact) {
        return (
            <button
                type="button"
                onClick={open}
                className={cn(
                    "flex w-full items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-xs",
                    "text-muted-foreground hover:bg-muted/50 transition-colors",
                    className,
                )}
            >
                <Search className="size-3.5" />
                <span className="flex-1 text-left">Search...</span>
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={open}
            className={cn(
                "flex w-full items-center gap-3 rounded-lg border bg-background px-4 py-2.5 text-sm",
                "text-muted-foreground hover:bg-muted/50 transition-colors",
                className,
            )}
        >
            <Search className="size-4" />
            <span className="flex-1 text-left">Search help articles...</span>
            <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">&#8984;</span>K
            </kbd>
        </button>
    );
}

// --- Backwards compat: standalone component with trigger + dialog ---

export function HelpSearch() {
    return (
        <HelpSearchProvider>
            <HelpSearchTrigger />
        </HelpSearchProvider>
    );
}
